import { useAssets } from '@/api/equipment/assets/getAssets';
import { useEvents } from '@/api/events/events/getEvents';
import { Event, Inspection, ModelID } from '@/types';
import { useMemo } from 'react';

export interface ShiftInspection {
  inspection: Inspection;
  equipmentLabel: string;
}

export interface ShiftData {
  events: Event[];
  inspections: ShiftInspection[];
  depotAddress: string | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Composes today's events for a coach with daily inspections for their assigned equipment.
 *
 * Data chain: coach's events (staff_id filter) -> event.activities -> asset_id
 * -> asset + asset.vehicle + asset.generators -> inspections where days_interval === 1
 */
export function useShiftData(locationId: ModelID, userId: ModelID): ShiftData {
  const {
    data: eventsRaw,
    isPending: eventsLoading,
    isError: eventsError,
  } = useEvents({
    location: locationId,
    search: {
      filter: 'today',
      staff_id: userId,
      status_not: 'Cancelled',
      include: 'activities,customer,assets,vips',
    },
    config: {
      staleTime: 1000 * 60 * 5,
    },
  });

  const events = useMemo(() => {
    if (!eventsRaw) return [];

    return [...eventsRaw]
      .filter((event) => event.status !== 'Cancelled' && event.status !== 'Canceled')
      .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
  }, [eventsRaw]);

  const assignedAssetIds = useMemo(() => {
    return [
      ...new Set(events.flatMap((event) => (event.activities ?? []).map((a) => a.asset_id))),
    ].filter(Boolean);
  }, [events]);

  const hasAssets = assignedAssetIds.length > 0;

  const {
    data: assets,
    isPending: assetsLoading,
    isError: assetsError,
  } = useAssets({
    location: locationId,
    search: {
      include:
        'inspections.logs,inspections.logs.results,generators,vehicle,vehicle.generators,depot',
      with_status: true,
    },
    config: {
      enabled: hasAssets,
      staleTime: 1000 * 60 * 60,
    },
  });

  const inspections = useMemo(() => {
    if (!assets || !hasAssets) return [];

    const assetIdSet = new Set<number>(assignedAssetIds);
    const relevantAssets = assets.filter((asset) => assetIdSet.has(Number(asset.asset_id)));

    const result: ShiftInspection[] = [];

    for (const asset of relevantAssets) {
      collectDailyInspections(result, asset.inspections, asset.name);

      if (asset.generators) {
        for (const generator of asset.generators) {
          collectDailyInspections(result, generator.inspections, generator.name);
        }
      }

      // Related vehicle inspections (vehicle is linked to asset via vehicle_id)
      if (asset.vehicle) {
        collectDailyInspections(
          result,
          asset.vehicle.inspections,
          asset.vehicle.name ?? asset.vehicle.full_name,
        );

        // Generators can be attached to vehicles too
        if (asset.vehicle.generators) {
          for (const generator of asset.vehicle.generators) {
            collectDailyInspections(result, generator.inspections, generator.name);
          }
        }
      }
    }

    return result;
  }, [assets, assignedAssetIds, hasAssets]);

  // Derive the depot address from the first assigned asset's depot
  const depotAddress = useMemo(() => {
    if (!assets || !hasAssets) return null;

    const assetIdSet = new Set<number>(assignedAssetIds);
    const firstAsset = assets.find((asset) => assetIdSet.has(Number(asset.asset_id)));
    const depot = firstAsset?.depot;

    if (!depot) return null;

    return [depot.address, depot.city, depot.state, depot.zipcode].filter(Boolean).join(', ');
  }, [assets, assignedAssetIds, hasAssets]);

  const isLoading = eventsLoading || (hasAssets && assetsLoading);
  const isError = eventsError || (hasAssets && assetsError);

  return { events, inspections, depotAddress, isLoading, isError };
}

function collectDailyInspections(
  result: ShiftInspection[],
  inspections: Inspection[] | undefined | null,
  equipmentLabel: string,
) {
  if (!inspections) return;

  for (const inspection of inspections) {
    if (inspection.days_interval === 1) {
      result.push({ inspection, equipmentLabel });
    }
  }
}
